/**
 * Spec 035 v0.4, step 2: the work of a unit as people see it — its board of orders, one order
 * whole (its result apart from the talk, its parts), a person's order to the head of a top unit
 * and the conversation with that head. People read everything; they give orders and write only to
 * the heads of top units.
 */

import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, newIdempotencyKey } from "@/api/client";
import {
  ORDER_EFFORTS,
  ORDER_MODELS,
  orderSettings,
  UnitWork,
  type DepartmentOut,
  type Effort,
  type MessageOut,
  type OrderOut,
} from "@/api/units";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { Caption, Chip, Pill } from "@/kit";
import { formatRelativePast } from "@/lib/format";
import { Markdown } from "@/lib/markdown";
import { boardOf, givenLine, MESSAGE_KIND_LABEL, orderStatus } from "@/lib/units";

const refused = (e: unknown): e is ApiError =>
  e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408;

/** A message longer than this starts folded in the conversation: a report is read on demand. */
const LONG_MESSAGE = 600;

function OrderRow({ order, onOpen }: { order: OrderOut; onOpen: (id: string) => void }) {
  const status = orderStatus(order);
  return (
    <li className="border-b border-p-border last:border-0">
      <button type="button" onClick={() => onOpen(order.id)} className="flex w-full flex-col gap-1 px-4 py-3 text-left press-surface">
        <span className="text-[15px] font-semibold leading-5 text-p-text">
          №{order.number} {order.title}
          {order.urgent && <span className="ml-2 text-meta font-semibold text-p-blocked">срочно</span>}
        </span>
        <span className="text-meta text-p-text-muted">
          {order.assignee.name} · {givenLine(order)}
          {order.parts > 0 && ` · частей: ${order.parts}`}
          {orderSettings(order) && ` · ${orderSettings(order)}`}
        </span>
        <span><Pill tone={status.tone}>{status.label}</Pill></span>
      </button>
    </li>
  );
}

/** The unit's board: open orders and those closed in the last two weeks. */
export function OrdersBoard({ unitId, onOpen }: { unitId: string; onOpen: (id: string) => void }) {
  const board = useQuery({ queryKey: ["departments", "orders", unitId], queryFn: () => UnitWork.board(unitId) });
  if (board.isLoading) return <SkeletonList count={2} />;
  if (board.isError) return <p className="text-meta text-p-blocked">Не удалось загрузить поручения.</p>;
  const columns = boardOf(board.data ?? []);
  if (!columns.length) return <p className="text-meta text-p-text-muted">Поручений нет.</p>;
  return (
    <div className="flex flex-col gap-3">
      {columns.map((column) => (
        <div key={column.key} className="flex flex-col gap-1">
          <span className="text-meta font-semibold text-p-text-muted">{column.title} · {column.orders.length}</span>
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {column.orders.map((order) => <OrderRow key={order.id} order={order} onOpen={onOpen} />)}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** `fold`: a long message shows its start and «Показать целиком» (the unit's conversation). */
function MessageItem({ message, fold = false }: { message: MessageOut; fold?: boolean }) {
  const kind = MESSAGE_KIND_LABEL[message.kind];
  const [whole, setWhole] = useState(false);
  const folded = fold && !whole && message.body_md.length > LONG_MESSAGE;
  return (
    <li className="flex flex-col gap-1 rounded-card border border-p-border bg-p-surface px-4 py-3">
      <span className="text-meta text-p-text-muted">
        {message.urgent && <span className="mr-1 font-semibold text-p-blocked">срочно ·</span>}
        <span className="font-semibold text-p-text">{message.author.name}</span>
        {message.via_agent && " через агента"}
        {kind && ` · ${kind}`}
        {message.order_number && ` · №${message.order_number}`}
        {" · "}
        {formatRelativePast(new Date(message.created_at))}
      </span>
      <div className={folded ? "max-h-48 overflow-hidden [mask-image:linear-gradient(to_bottom,black_60%,transparent)]" : undefined}>
        <Markdown text={message.body_md} className="text-body text-p-text" />
      </div>
      {folded && (
        <button type="button" className="min-h-11 self-start text-meta font-semibold text-p-link" onClick={() => setWhole(true)}>
          Показать целиком
        </button>
      )}
    </li>
  );
}

/** One order whole: what was asked, the result apart from the talk, its parts, the messages. */
export function OrderDetailSheet({ orderId, canCancel, onClose, onOpen }: {
  orderId: string;
  canCancel: boolean;
  onClose: () => void;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const one = useQuery({ queryKey: ["departments", "order", orderId], queryFn: () => UnitWork.order(orderId) });
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const order = one.data;
  const open = order && !["done", "rejected", "cancelled"].includes(order.state);

  const cancel = async () => {
    if (!order) return;
    setCancelling(true);
    try {
      await UnitWork.cancel(order.id, order.version, reason.trim() || null);
      toast.success(`№${order.number} отменено`);
      void qc.invalidateQueries({ queryKey: ["departments"] });
      onClose();
    } catch (e) {
      toast.error(refused(e) ? e.message : "Ответ сервера не дошёл: открой поручение заново.");
      void qc.invalidateQueries({ queryKey: ["departments"] });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title={order ? `№${order.number} ${order.title}` : "Поручение"}
      footer={canCancel && open ? (
        <div className="space-y-2">
          <TextField label="Почему отменяешь" value={reason} maxLength={5000} onChange={(e) => setReason(e.target.value)} />
          <Button block variant="secondary" loading={cancelling} loadingText="Отменяем…" onClick={() => void cancel()}>
            Отменить поручение
          </Button>
        </div>
      ) : undefined}>
      {!order ? (
        one.isError ? <p className="text-meta text-p-blocked">Не удалось открыть поручение.</p> : <SkeletonList count={2} />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-meta text-p-text-muted">
            {order.unit_name} · делает {order.assignee.name} · {givenLine(order)}
          </p>
          {orderSettings(order) && (
            <p className="text-meta text-p-text-muted">В своём запуске: {orderSettings(order)}</p>
          )}
          <span><Pill tone={orderStatus(order).tone}>{orderStatus(order).label}</Pill></span>
          {order.body_md && <Markdown text={order.body_md} className="text-body text-p-text" />}
          <div className="flex flex-col gap-1">
            <Caption>Ожидаемый результат</Caption>
            <Markdown text={order.expected_md} className="text-body text-p-text" />
          </div>
          {order.reason_md && (
            <p className="text-meta text-p-needs">{order.state === "input_required" ? "Нужно: " : "Причина: "}{order.reason_md}</p>
          )}
          {order.result_md && (
            <div className="flex flex-col gap-1 rounded-card border border-p-border bg-p-done-tint px-4 py-3">
              <Caption>Результат</Caption>
              <Markdown text={order.result_md} className="text-body text-p-text" />
            </div>
          )}
          {order.parts_list.length > 0 && (
            <div className="flex flex-col gap-1">
              <Caption>Части</Caption>
              <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
                {order.parts_list.map((part) => <OrderRow key={part.id} order={part} onOpen={onOpen} />)}
              </ul>
            </div>
          )}
          {order.messages.length > 0 && (
            <div className="flex flex-col gap-2">
              <Caption>Переписка</Caption>
              <ul className="flex flex-col gap-2">{order.messages.map((m) => <MessageItem key={m.id} message={m} />)}</ul>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

/** A person's order to the head of a top unit, with the expected result. */
export function GiveOrderSheet({ unit, onClose }: { unit: DepartmentOut; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [expected, setExpected] = useState("");
  const [urgent, setUrgent] = useState(false);
  const [model, setModel] = useState<string | null>(null);
  const [effort, setEffort] = useState<Effort | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const attempt = useRef<string | null>(null);
  const fresh = () => { attempt.current = null; };

  const give = async () => {
    setError(undefined);
    setSaving(true);
    try {
      const key = (attempt.current ??= newIdempotencyKey());
      const order = await UnitWork.give(
        unit.id,
        { title: title.trim(), body_md: body.trim() || null, expected_md: expected.trim(), urgent, model, effort },
        key,
      );
      attempt.current = null;
      void qc.invalidateQueries({ queryKey: ["departments"] });
      toast.success(order.waits_launch ? `№${order.number} ждёт запуска руководителя` : `№${order.number} передано руководителю`);
      onClose();
    } catch (e) {
      if (refused(e)) attempt.current = null;
      setError(refused(e) ? e.message : "Ответ сервера не дошёл. Нажми ещё раз — второго поручения не будет.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title={`Поручение: ${unit.head?.name ?? unit.name}`}
      footer={
        <div className="space-y-2">
          {error && <p role="alert" className="text-meta text-p-blocked">{error}</p>}
          <Button block variant="strong" loading={saving} loadingText="Передаём…"
            disabled={!title.trim() || !expected.trim()} onClick={() => void give()}>
            Поручить
          </Button>
        </div>
      }>
      <div className="flex flex-col gap-3">
        <TextField label="Что сделать" value={title} maxLength={200} onChange={(e) => { fresh(); setTitle(e.target.value); }} />
        <TextArea label="Подробности" value={body} rows={4} maxLength={20000} onChange={(e) => { fresh(); setBody(e.target.value); }} />
        <TextArea label="Ожидаемый результат" hint="Что будет считаться выполненным" value={expected} rows={3} maxLength={5000}
          onChange={(e) => { fresh(); setExpected(e.target.value); }} />
        <label className="flex min-h-11 items-center gap-3 text-body text-p-text">
          <input type="checkbox" className="h-5 w-5" checked={urgent} onChange={() => { fresh(); setUrgent((u) => !u); }} />
          Срочно
        </label>
        <div className="flex flex-col gap-1.5">
          <Caption>Модель</Caption>
          <div className="flex flex-wrap gap-2">
            <Chip on={model === null} onClick={() => { fresh(); setModel(null); }}>как в карточке</Chip>
            {ORDER_MODELS.map((m) => (
              <Chip key={m} on={model === m} onClick={() => { fresh(); setModel(m); }}>{m}</Chip>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Caption>Effort</Caption>
          <div className="flex flex-wrap gap-2">
            <Chip on={effort === null} onClick={() => { fresh(); setEffort(null); }}>по умолчанию</Chip>
            {ORDER_EFFORTS.map((e) => (
              <Chip key={e} on={effort === e} onClick={() => { fresh(); setEffort(e); }}>{e}</Chip>
            ))}
          </div>
          {(model || effort) && (
            <p className="text-meta text-p-text-muted">
              С моделью или effort поручение делается в своём запуске: сотрудник возьмёт его отдельно, с этими настройками.
            </p>
          )}
        </div>
      </div>
    </Sheet>
  );
}

/** A top unit's conversation: its people and its head. Long reports start folded. */
export function UnitConversation({ unit }: { unit: DepartmentOut }) {
  const qc = useQueryClient();
  const toast = useToast();
  const talk = useQuery({ queryKey: ["departments", "conversation", unit.id], queryFn: () => UnitWork.conversation(unit.id) });
  const [text, setText] = useState("");
  const [question, setQuestion] = useState(false);
  const [sending, setSending] = useState(false);
  const attempt = useRef<string | null>(null);

  const send = async () => {
    setSending(true);
    try {
      const key = (attempt.current ??= newIdempotencyKey());
      await UnitWork.write(unit.id, { body_md: text.trim(), kind: question ? "question" : "message" }, key);
      attempt.current = null;
      setText("");
      void qc.invalidateQueries({ queryKey: ["departments", "conversation", unit.id] });
    } catch (e) {
      if (refused(e)) attempt.current = null;
      toast.error(refused(e) ? e.message : "Ответ сервера не дошёл. Нажми ещё раз — второго сообщения не будет.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {talk.isLoading ? <SkeletonList count={2} /> : (talk.data ?? []).length ? (
        <ul className="flex flex-col gap-2">{(talk.data ?? []).map((m) => <MessageItem key={m.id} message={m} fold />)}</ul>
      ) : (
        <p className="text-meta text-p-text-muted">Пока тихо. Руководитель пишет сюда отчёты — по запросу, утром и если что-то срочно.</p>
      )}
      <TextArea label={`Написать ${unit.head?.name ?? "руководителю"}`} value={text} rows={3} maxLength={20000}
        onChange={(e) => { attempt.current = null; setText(e.target.value); }} />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex min-h-11 items-center gap-2 text-meta text-p-text">
          <input type="checkbox" className="h-5 w-5" checked={question} onChange={() => { attempt.current = null; setQuestion((q) => !q); }} />
          Это вопрос
        </label>
        <Button size="compact" variant="strong" loading={sending} loadingText="Отправляем…"
          disabled={!text.trim() || !unit.head} onClick={() => void send()}>
          Отправить
        </Button>
      </div>
    </div>
  );
}
